import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import {
  type ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostData,
  type ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostError,
  type ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostResponse,
  complexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPost,
} from '../../generated';

export type ComplexSearchPdpsMutationVariables = ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostData['path'] &
  ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostData['body'];

export type ComplexSearchPdpsMutation = {
  data: ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useComplexSearchPdpsMutation = <
  TError = ComplexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPostError,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<ComplexSearchPdpsMutation, TError, ComplexSearchPdpsMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<ComplexSearchPdpsMutation, TError, ComplexSearchPdpsMutationVariables, TContext>({
    mutationKey: ['ComplexSearchPdps'],
    mutationFn: async ({ portfolio_id, ...variables }: ComplexSearchPdpsMutationVariables) => {
      const { data, response } = await complexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPost({
        path: { portfolio_id },
        body: variables,
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useComplexSearchPdpsMutation.fetcher = (
  { portfolio_id, ...variables }: ComplexSearchPdpsMutationVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await complexSearchPdpsV1PortfoliosPortfolioIdPdpsSearchPost({
      path: { portfolio_id },
      body: variables,
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
